import { Injectable } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { LanguageService } from './../core/language.service';
import { FilterProperty } from './filter-property';
import {
  CATEGORIES_FOR_AUDIENCE,
  CATEGORIES_FOR_PEOPLE,
  CATEGORIES_FOR_VISITS,
  CriteriaElement,
  CriteriaGroup,
  UtmCampaign,
} from './global-criteria-list';
import { InputSearchCriteria } from 'app/dashboard/leads/visits-request';
import { SelectionsService } from './../shared/services/selections.service';
import { SelectionType } from './segments';
import { AutomationHelpers } from '../automations/automation-helpers';
import { TranslateService } from '@ngx-translate/core';
import { WidgetService } from '../shared/services/widget.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';

@Injectable()
export class LoadSuggestionsService {
  private readonly logger: Logger;
  private readonly apiBaseUrl: string;

  private goals: FilterProperty[];
  private campaigns: FilterProperty[];
  private labels: FilterProperty[];
  private profilings: FilterProperty[];
  private visitedPages: FilterProperty[];
  private widgets: FilterProperty[];

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    private languageService: LanguageService,
    private selectionsService: SelectionsService,
    private readonly widgetService: WidgetService,
    private translateService: TranslateService,
    private readonly http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('LoadSuggestionsService');
    this.apiBaseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
  }

  public clearStaticCaches() {
    this.goals = undefined;
    this.campaigns = undefined;
    this.labels = undefined;
    this.profilings = undefined;
    this.visitedPages = undefined;
  }

  public getGlobalCriteriaListForAudience(): Array<CriteriaGroup> {
    return CATEGORIES_FOR_AUDIENCE;
  }

  public async getGlobalCriteriaListForView(pageView: SelectionType): Promise<Array<CriteriaGroup>> {
    if (pageView === SelectionType.Visits) {
      const categories = CATEGORIES_FOR_VISITS;
      return categories;
    } else if (pageView === SelectionType.People) {
      const categories = CATEGORIES_FOR_PEOPLE;
      return categories;
    } else if (pageView === SelectionType.Audience) {
      return CATEGORIES_FOR_AUDIENCE;
    } else {
      this.logger.error('Unsupported pageView: ' + pageView);
    }
  }

  public async getCriteriaElement(type: string, criteriaGroups: CriteriaGroup[]) {
    let element: CriteriaElement = undefined;

    criteriaGroups.forEach((criteriaGroup) => {
      criteriaGroup.options.forEach((option) => {
        // search main category
        if (option.name === type) {
          element = option;
        }
      });
    });

    return element;
  }

  public async getGoalsAsync(): Promise<FilterProperty[]> {
    if (!this.goals) {
      this.goals = await this.selectionsService.getGoalsAsync();
    }

    return this.goals;
  }

  public async getCampaignsAsync(): Promise<FilterProperty[]> {
    if (!this.campaigns) {
      const response = await this.requester.getAsync<UtmCampaign[]>(TriggerbeeApi.Gateway, 'utm-campaigns');
      if (response.isPositive()) {
        this.campaigns = response.data.map((x) => new FilterProperty().set(x.name, x.name));
      } else {
        this.logger.error(`Could not get Campaigns : ${response.toString()}`);
      }
    }
    return this.campaigns;
  }

  public async getLabelsAsync(): Promise<FilterProperty[]> {
    if (!this.labels) {
      this.labels = await this.selectionsService.getLabelsAsync();
    }

    return this.labels;
  }

  public async getProfilingTopicsAsync(): Promise<FilterProperty[]> {
    const interestProfiles = await this.http.get(`${this.apiBaseUrl}/interest-profiles`).toPromise();
    return interestProfiles.map((x) => {
      return <FilterProperty>{
        id: x.id,
        displayName: x.name,
      };
    });
  }

  public async getWidgetsAsync(): Promise<FilterProperty[]> {
    if (!this.widgets) {
      this.widgets = await this.selectionsService.getWidgetsAsFilterPropertiesAsync();
    }

    return this.widgets;
  }

  public async convertIdToValueList(type: string, ids: any) {
    const valueList = [];

    if (type === 'profilings') {
      if (!this.profilings) {
        this.profilings = await this.getProfilingTopicsAsync();
      }

      if (this.profilings) {
        for (let i = 0; i < ids.length; i++) {
          const profiling = this.profilings.find((p) => p.id === ids[i]);
          valueList.push(profiling ? profiling.displayName : ids[i]);
        }
      }
    } else if (type === 'visitedPages') {
      if (!this.visitedPages) {
        await this.getVisitedPages();
      }

      if (this.visitedPages) {
        for (let i = 0; i < ids.length; i++) {
          const visitedPage = this.visitedPages.find((p) => p.id === ids[i]);
          valueList.push(visitedPage ? visitedPage.displayName : ids[i]);
        }
      }
    }

    return valueList;
  }

  public async getVisitedPages(): Promise<FilterProperty[]> {
    if (!this.visitedPages) {
      this.visitedPages = await this.selectionsService.getVisitedPages();
    }

    return this.visitedPages;
  }

  public async getSearchExpression(inputSearchCriteria: InputSearchCriteria) {
    if (inputSearchCriteria.type.toLocaleLowerCase().indexOf('device') !== -1) {
      // return custom expressions and do not use logic
      return inputSearchCriteria.displayExp;
    }

    if (inputSearchCriteria.type.toLocaleLowerCase().indexOf('identified') !== -1) {
      if (inputSearchCriteria.input === true) {
        return await this.languageService.translateAsync('visits.criteriaProperties.identified');
      } else if (inputSearchCriteria.input === false) {
        return await this.languageService.translateAsync('visits.criteriaProperties.notIdentified');
      }
    }

    if (
      inputSearchCriteria.type.toLocaleLowerCase().indexOf('referrerdomain') === 0 &&
      inputSearchCriteria.input.toString().indexOf('facebook') === 0
    ) {
      return 'Facebook';
    }

    if (
      inputSearchCriteria.type.toLocaleLowerCase().indexOf('referrertype') === 0 &&
      inputSearchCriteria.input.toString().indexOf('advertising') === 0
    ) {
      return await this.languageService.translateAsync('visits.criteriaProperties.paidTraffic');
    }

    if (
      inputSearchCriteria.type.toLocaleLowerCase().indexOf('labels') === 0 &&
      inputSearchCriteria.input.toString().indexOf('Isp') !== -1 &&
      inputSearchCriteria.input.toString().indexOf('Uninteresting') !== -1 &&
      inputSearchCriteria.input.toString().indexOf('Coworker') !== -1
    ) {
      return await this.languageService.translateAsync('defaultSegments.MostInteresting');
    }

    let operatorDisplayText;

    if (inputSearchCriteria.operator != null) {
      switch (inputSearchCriteria.operator.toString()) {
        case '10':
          operatorDisplayText = '*=';
          break;
        case '11':
          operatorDisplayText = '=*';
          break;
        case '2':
          operatorDisplayText = '*=*';
          break;
        case '0':
          operatorDisplayText = '=';
          break;
        case '1':
          operatorDisplayText = '!=';
          break;
        case '6':
          operatorDisplayText = '>';
          break;
        case '7':
          operatorDisplayText = '<';
          break;
        default:
          operatorDisplayText = '=';
          break;
      }
    } else {
      operatorDisplayText = '';
    }

    const propertyName = await this.languageService.translateAsync(
      'visits.criteriaProperties.' + inputSearchCriteria.type,
    );
    let value = inputSearchCriteria.input ? inputSearchCriteria.input : '';

    if (
      (inputSearchCriteria.type === 'profilings' || inputSearchCriteria.type === 'visitedPages') &&
      Array.isArray(inputSearchCriteria.input)
    ) {
      // ids, values are different only profilings, visited pages
      value = await this.convertIdToValueList(inputSearchCriteria.type, inputSearchCriteria.input);
    }
    if (inputSearchCriteria.type === 'goals') {
      const widgets = await this.selectionsService.getAllWidgetsSimpleInfosAsync();
      const goals = [];
      if (!Array.isArray(value)) value = [value];
      value.forEach((item) => {
        goals.push(AutomationHelpers.prettifySingleGoalName(this.translateService, widgets, item, true, true));
      });

      if (inputSearchCriteria.operator === 12) {
        operatorDisplayText = '';
      }

      return `${propertyName} ${operatorDisplayText} ${goals}`;
    }
    if (inputSearchCriteria.type.startsWith('widget')) {
      const widgets = await this.widgetService.getAllWidgetsSimpleInfo().toPromise();
      const campaignNames = [];
      inputSearchCriteria.input.forEach((input) => {
        campaignNames.push(widgets.find((x) => x.id.toString() === input).campaignName);
      });
      return `${propertyName} ${operatorDisplayText} ${campaignNames}`;
    }
    if (inputSearchCriteria.type === 'consent' && inputSearchCriteria.input === null) {
      return `${propertyName} ${operatorDisplayText} any`;
    }
    return `${propertyName} ${operatorDisplayText} ${value}`;
  }
}
